import { useEffect, useState } from 'react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/public/v1/service.pb';
import { ImageSection as ImageSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useModalControl } from '@frontend/design-system';
import { useComposerSection } from '../../hooks';
import { ComposerSection } from '../../types';
import { convertMediaToImage, getFrameStyle } from '../../utils';
import { ImageOutput } from '../outputs';
import { ImageSettings } from '../settings';
import { ImageTool } from '../tools';
import { BaseComposerSection } from './floating-toolbar';
import {
  createImagesWithDimensions,
  ImageWithDimensions,
  JustifiedLayout,
  newImageWithDimensions,
  removeDimensions,
  SelectImageModal,
} from './image-elements';

export const ImageSection: ComposerSection<ImageSectionType> = ({
  corners = 'square',
  frame,
  height = 'auto',
  imageCount = 1,
  imageSpacing = 0,
  images = [],
}) => {
  const { t } = useTranslation('content-composer');
  const [imagesWithDimensions, setImagesWithDimensions] = useState(() =>
    createImagesWithDimensions(imageCount, images)
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>();
  const { setProps } = useComposerSection<ImageSectionType>();
  const { modalProps, triggerProps } = useModalControl();

  useEffect(() => {
    // If the user has increased or decreased the imageCount in the tool settings, update imagesWithDimensions to match
    if (imageCount !== imagesWithDimensions.length) {
      setImagesWithDimensions(createImagesWithDimensions(imageCount, imagesWithDimensions));
    }
  }, [imageCount, imagesWithDimensions]);

  const loadImage = (imageIndex: number, width: number, height: number) => {
    setImagesWithDimensions((previousImagesWithDimensions) => {
      const updatedImagesWithDimensions = [...previousImagesWithDimensions];
      updatedImagesWithDimensions[imageIndex] = {
        ...newImageWithDimensions(updatedImagesWithDimensions[imageIndex]),
        width,
        height,
      };
      return updatedImagesWithDimensions;
    });
  };

  const saveImages = (updatedImagesWithDimensions: ImageWithDimensions[]) => {
    const updatedImages = updatedImagesWithDimensions.map((imageWithDimensions) =>
      removeDimensions(imageWithDimensions)
    );
    setProps((props) => (props.images = updatedImages));
    setImagesWithDimensions(updatedImagesWithDimensions);
  };

  const deselectImage = () => {
    setSelectedImageIndex(undefined);
  };

  const removeImage = (imageIndex: number) => {
    setSelectedImageIndex(imageIndex);
    const updatedImagesWithDimensions = [...imagesWithDimensions];
    updatedImagesWithDimensions[imageIndex] = newImageWithDimensions();
    saveImages(updatedImagesWithDimensions);
  };

  const startReplaceImage = (imageIndex: number) => {
    setSelectedImageIndex(imageIndex);
    triggerProps.onClick();
  };

  const finishReplaceImage = (media?: Media) => {
    if (!media || selectedImageIndex === undefined) return;
    const updatedImagesWithDimensions = [...imagesWithDimensions];
    updatedImagesWithDimensions[selectedImageIndex] = newImageWithDimensions(convertMediaToImage(media));
    saveImages(updatedImagesWithDimensions);
  };

  return (
    <BaseComposerSection css={getFrameStyle(frame)} title={t('Image')} onClick={deselectImage}>
      <JustifiedLayout
        alignment={frame?.alignment}
        corners={corners}
        height={height}
        imageSpacing={imageSpacing}
        images={imagesWithDimensions}
        onLoad={loadImage}
        onRemove={removeImage}
        onReplace={startReplaceImage}
        onSelect={setSelectedImageIndex}
        selectedImageIndex={selectedImageIndex}
      />
      <SelectImageModal modalProps={modalProps} onSelect={finishReplaceImage} />
    </BaseComposerSection>
  );
};

ImageSection.craft = {
  props: {
    corners: 'square',
    frame: {
      backgroundColor: theme.colors.neutral5.toUpperCase(),
      paddingBottom: '24px',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '24px',
    },
    height: 'auto',
    imageCount: 1,
    imageSpacing: 8,
    sectionType: 'ImageSection',
  },
  related: {
    output: ImageOutput,
    settings: ImageSettings,
    tool: ImageTool,
  },
};
